<template>
  <form
    @submit.prevent
    v-if="!loading"
    :class="['px-4 md:px-6 bg-form-grey', null === getActiveCancellation ? 'py-4' : 'pb-3']"
  >
    <div
      v-if="null !== getActiveCancellation"
      class="pt-6"
    >
      <h2 class="font-bold text-lg text-slate-600">
        {{ `${$t('messages_nl.cancellation.view.steps.form.1')}` }}
      </h2>

      <hr class="border-t-1 border-gray-300 mt-5" />
    </div>
    <!-- select box: company division selection -->
    <div v-if="showCancellationChoices">
      <label :class="['pt-4 text-blue text-sm visible !text-slate-600 pb-0 font-normal', { 'hidden': null === getActiveCancellation }]">
        {{ $t('messages_nl.label.cancellationType') }}
      </label>
      <adivare-select-two
        id="cancellationChoice"
        name="cancellationChoice"
        v-model="cancellationChoice"
        :placeholder="$t('messages_nl.partials.letter.select2.placeholder')"
        ref="cancellationChoiceSelect"
        property-name="subCancellationSelection"
        :error-message="$t('messages_nl.partials.letter.select2.error')"
      />
    </div>
    <template v-if="null !== getActiveCancellation">
      <div class="grid grid-cols-8 gap-x-5 gap-y-2 my-2">

        <!-- date of cancellation -->
        <div
          v-if="showCancellationDate"
          class="block col-span-full"
        >
          <label
            class="p-0 text-blue text-sm visible font-normal !text-slate-600"
            for="cancellationDatePicker"
          >
            {{ $t('messages_nl.letter.content.cancel.cancelDate') }}
          </label>
          <datepicker
            id="cancellationDatePicker"
            v-model="cancellationDate"
            :placeholder="$t('messages_nl.letter.content.cancel.cancelDate')"
            inputFormat="dd-MM-yyyy"
            :lowerLimit="new Date()"
            :locale="datepickerLocale"
            class="form--texttype border-1 border-form-border-dark-grey placeholder-gray-400 w-full"
          />
        </div>

        <!-- form fields -->
        <component
          v-for="(formField, index) in orderedFormFields"
          :key="index"
          :is="formField.component"
          :property-name="formField.propertyName"
          :input-class="`${formField.inputClass} w-full`"
          :legend-class="formField.legendClass"
          :outer-class="formField.outerClass"
          :label-key="formField.translationKey"
          :label-text="formField.name"
          :form-type="formField.formType"
          :minlength="formField.minlength"
          :maxlength="formField.maxlength"
          :optional="formField.optional"
          :autocomplete="formField.autocomplete"
          :show-property="formField.showProperty"
        />

        <!-- input field: IBAN -->
        <component
          v-if="0 !== getSelectedPaymentPrice"
          :is="getIbanParameters.component"
          :property-name="getIbanParameters.propertyName"
          :input-class="`${getIbanParameters.inputClass} w-full`"
          :legend-class="getIbanParameters.legendClass"
          :outer-class="getIbanParameters.outerClass"
          :label-key="getIbanParameters.translationKey"
          :label-text="getIbanParameters.name"
          :form-type="getIbanParameters.formType"
          :minlength="getIbanParameters.minlength"
          :maxlength="getIbanParameters.maxlength"
          :optional="getIbanParameters.optional"
        />

        <!-- textarea: Reason to Cancel -->
        <component
          :is="getReasonToCancelParameters.component"
          :property-name="getReasonToCancelParameters.propertyName"
          :input-class="`${getReasonToCancelParameters.inputClass} w-full`"
          :legend-class="getReasonToCancelParameters.legendClass"
          :outer-class="getReasonToCancelParameters.outerClass"
          :label-key="getReasonToCancelParameters.translationKey"
          :label-text="getReasonToCancelParameters.name"
          :form-type="getReasonToCancelParameters.formType"
          :minlength="getReasonToCancelParameters.minlength"
          :maxlength="getReasonToCancelParameters.maxlength"
          :optional="getReasonToCancelParameters.optional"
          :rows="getReasonToCancelParameters.rows"
          :legendTransKey="getReasonToCancelParameters.legendTransKey"
          :customErrorMessage="reasonToCancelErrorMessage"
        />

        <!-- checkbox: do not want to receive adds -->
        <div class="flex flex-nowrap col-span-full mt-6">
          <input
            v-model="customerRightToResist"
            id="rightToResist"
            class="col-span-full mr-2 form--choicetype-checkbox__checkbox-dark mt-0.5"
            type="checkbox"
          >
          <label
            for="rightToResist"
           >
            {{ $t('messages_nl.label.cancellation.rightToResist.withCompany', {'companyName': companyName}) }}
          </label>
        </div>
      </div>
    </template>
  </form>
</template>

<script>
import AdivareFlashes from "../ui/flashes";
import AdivareDefaultFormElement from "./partials/default-form-element";
import AdivareAddressFormElement from "./partials/address-form-element";
import AdivareDateFormElement from "./partials/date-form-element";
import AdivareEmailFormElement from "./partials/email-form-element";
import AdivarePostcodeFormElement from "./partials/postcode-form-element";
import AdivareIbanFormElement from "./partials/iban-form-element";
import getFormObject from './partials/form-object';
import genericFormObject from './partials/generic-form-object';
import AdivareSelectTwo from "../ui/select-two";
import Datepicker from 'vue3-datepicker';
import AdivareAlert from '../../../components/alert';
import readCookie, { customerData } from "../../../../js/util/cookie";
import AdivareGenericCancellationTypeahead from "./partials/generic-cancellation-typeahead";
import { mapGetters } from 'vuex'
import { nl, enUS } from 'date-fns/locale';

const cookieKeys = [
  'customerFirstName',
  'customerLastName',
  'customerCity',
  'customerStreet',
  'customerHouseNumber',
  'customerHouseNumberExtension',
  'customerEmail',
  'customerCountry',
  'customerPostcode',
  'customerRightToResist',
];

export default {
  name: "adivare-letter-form",
  components: {
    AdivareGenericCancellationTypeahead,
    AdivareSelectTwo,
    AdivareDefaultFormElement,
    AdivareDateFormElement,
    AdivareEmailFormElement,
    AdivarePostcodeFormElement,
    AdivareIbanFormElement,
    AdivareAddressFormElement,
    AdivareFlashes,
    Datepicker,
    AdivareAlert,
  },
  props: {
    loading: {
      type: Boolean,
      default: false,
      required: false,
    }
  },
  data() {
    return {
      cancelAtDateState: false,
      cancelDate: new Date(),
    };
  },
  computed: {
    datepickerLocale() {
      return this.$i18n.locale === 'nl-NL' ? nl : enUS;
    },
    ...mapGetters([
      'getReasonToCancelParameters',
      'getReasonToCancelFormField',
      'getIbanParameters',
      'getAdditionalFormFields',
      'getCancellationOptions',
      'getActiveCancellation',
      'getSelectedPaymentPrice'
    ]),
    cancellationChoice: {
      get() {
        try {
          return this.getActiveCancellation.id;
        } catch (e) {
          return null;
        }
      },
      set(id) {
        this.$store.dispatch('updateActiveCancellation', { id });
      }
    },
    cancellationDate: {
      get() {
        return this.cancelDate;
      },
      set(value) {
        this.cancelDate = value;
        this.$store.dispatch('updateCancellationDate', value);
      }
    },
    customerRightToResist: {
      get() {
        return this.$store.getters.getFormField('customerRightToResist');
      },
      set(value) {
        this.$store.dispatch('updateFormField', {
          name: 'customerRightToResist',
          value,
        })
        this.$store.commit('updateFormFieldValidity', {
          name: 'customerRightToResist',
          valid: true,
        })
      }
    },
    formFields() {
      if (true === this.$store.getters.generic) {
        return {...genericFormObject, ...getFormObject};
      }

      return getFormObject;
    },
    /**
     * This is the hack required to make opzegdatum work as additional form field.
     * @returns {[]}
     */
    orderedFormFields() {
      return [
        ...this.getRegularFormFields(this.formFields),
        ...this.getAdditionalFormFieldsMutation(this.getAdditionalFormFields)
      ];
    },
    showCancellationChoices() {
      return this.numberOfOptions > 0;
    },
    showErrors() {
      return true === this.$store.showErrors;
    },
    reasonToCancelErrorMessage() {
      const textLength = String(this.getReasonToCancelFormField).length;
      if (textLength < this.getReasonToCancelParameters.minlength) {
        return this.$t('messages_nl.label.cancellation.userLetter.reasonToCancel.minError');
      }

      if (textLength > this.getReasonToCancelParameters.maxlength) {
        return this.$t('messages_nl.label.cancellation.userLetter.reasonToCancel.maxError');
      }

      return null;
    },
    showCancellationDate() {
      const additionalFormFields = this.getAdditionalFormFields;
      for (const form in additionalFormFields) {
        if ('opzegdatum' === additionalFormFields[form]?.slug) {
          return true;
        }
      }

      return false;
    },
    numberOfOptions() {
      return Object.keys(this.getCancellationOptions).length;
    },
    activeCancellation() {
      return this.cancellationChoice === null ? this.$store.getters.getCancellation :
        this.$store.getters.getSubCancellation(this.cancellationChoice);
    },
    companyName() {
      try {
        return this.$store.getters.getActiveCancellation.companyName ?? '';
      } catch (e) {
        return '';
      }
    },
  },
  mounted() {
    // pre-seed the formFields object with predefined existing formFields, everything else is labeled as additional.
    for (const propertyName in this.formFields) {
      this.$store.dispatch('addFormField', {
        name: propertyName,
        value: null,
        optional: true === this.formFields[propertyName].optional,
      });
    }
    this.$store.commit('updateFormFieldValidity', {
      name: 'customerHouseNumberExtension',
      valid: true,
    });
    this.$store.commit('addFormField', {
      name: 'customerSignature',
      value: null,
      optional: false,
    });
    this.$store.commit('addFormField', {
      name: 'customerRightToResist',
      value: true,
      optional: false,
    });
    this.$store.commit('updateFormFieldValidity', {
      name: 'customerRightToResist',
      valid: true,
    });
    this.$store.commit('updateFormFieldValidity', {
      name: 'subCancellationSelection',
      valid: false === this.showCancellationChoices,
    });
    this.readCookieData();
  },
  methods: {
    getComponent(type) {
      return 'date' === type ? 'adivare-date-form-element' : 'adivare-default-form-element';
    },
    getAdditionalType(type) {
      return 'int' === type ? 'number' : type;
    },
    getRegularFormFields(regularFormFields) {
      const orderedFormFields = [];
      for (const fieldName in regularFormFields) {
        const preparedField = regularFormFields[fieldName];
        preparedField['propertyName'] = fieldName;
        orderedFormFields.push(preparedField);
      }

      return orderedFormFields;
    },
    getAdditionalFormFieldsMutation(additionalFormFields) {
      const orderedFormFields = [];
      for (const fieldName in additionalFormFields) {
        if ('opzegdatum' === fieldName) {
          continue;
        }
        const preparedField = additionalFormFields[fieldName];
        preparedField['propertyName'] = fieldName;
        const preparedType = this.getAdditionalType(preparedField['type'])
        preparedField['type'] = preparedType;
        preparedField['optional'] = !preparedField['required'];
        preparedField['inputClass'] = 'form--texttype border-1 border-form-border-dark-grey placeholder-gray-400';
        preparedField['legendClass'] = 'font-normal !text-slate-600';
        preparedField['outerClass'] = 'block col-span-full';
        preparedField['component'] = this.getComponent(preparedType);
        orderedFormFields.push(preparedField);
      }

      return orderedFormFields;
    },
    readCookieData() {
      let data = {};

      try {
        data = readCookie(customerData);
      } catch (e) {
        return;
      }

      // Prefill the form with customerData found in the cookies and set field state validity.
      for (const [name, value] of Object.entries(data)) {
        this.$store.dispatch('updateFormField', {
          name,
          value,
        });
        this.$store.commit('updateFormFieldValidity', {
          name,
          valid: true,
        });
      }

      // If the postcode and houseNumber are known, we can validate the address, causing city to be filled.
      if ('customerPostcode' in data && 'customerHouseNumber' in data) {
        this.$store.dispatch('validateAddress');
      }
    },
  },
}
</script>
